import { Facebook, Globe, Instagram } from 'lucide-react';

import { useApp } from '@/contexts/AppContext';
import { trackAdultingSocialLinkClick } from '@/lib/socialLinkTracking';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import { ADULTING_SOCIAL_LINKS, type SocialLinkItem } from '../../shared/socialLinks';

type AdultingSocialLinksCardProps = {
  sourceScreen: string;
  helperText?: string;
  includeWebsite?: boolean;
};

function iconForLink(link: SocialLinkItem) {
  switch (link.platform) {
    case 'facebook':
      return <Facebook size={16} />;
    case 'instagram':
      return <Instagram size={16} />;
    case 'website':
      return <Globe size={16} />;
    default:
      return null;
  }
}

export default function AdultingSocialLinksCard({
  sourceScreen,
  helperText = 'Adulting stays utility-first, so these links sit quietly here for updates rather than competing with the work you came to do.',
  includeWebsite = false,
}: AdultingSocialLinksCardProps) {
  const { user } = useApp();
  const links = includeWebsite
    ? ADULTING_SOCIAL_LINKS
    : ADULTING_SOCIAL_LINKS.filter((link) => link.platform !== 'website');

  const handleOpen = (link: SocialLinkItem) => {
    window.open(link.url, '_blank', 'noopener,noreferrer');
    void trackAdultingSocialLinkClick({
      userId: user?.id,
      sourceScreen,
      destinationPlatform: link.platform,
      destinationUrl: link.url,
    });
  };

  return (
    <AppCard>
      <AppCardHeader>
        <AppSectionHeader
          title="Follow Adulting"
          subtitle="Facebook and Instagram stay available here without becoming a core navigation path."
        />
      </AppCardHeader>
      <AppCardBody gap="$md">
        <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted">
          {helperText}
        </AppText>
        <div className="grid gap-3 sm:grid-cols-2">
          {links.map((link) => (
            <AppButton
              key={link.platform}
              type="button"
              tone="outline"
              size="sm"
              width="100%"
              onPress={() => handleOpen(link)}
            >
              <span className="inline-flex items-center gap-2">
                {iconForLink(link)}
                <span>{link.label}</span>
              </span>
            </AppButton>
          ))}
        </div>
      </AppCardBody>
    </AppCard>
  );
}
